import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { t } from '../../../i18n/i18n';
import './Certificates.scss';

/**
 * The Certificates page while `GET` for the list is in flight.
 *
 * It reuses the real layout classes — `cert-hero`, `cert-list`, `cert-grid`,
 * `cert-item` — rather than approximating them, so the hero holds its height
 * and the grid its columns at every breakpoint, and the loaded page drops into
 * the same boxes. Only the contents of each box are stand-ins.
 *
 * The hero is worth skeletoning rather than leaving out: it is the tallest
 * thing on the page, so a list that renders first and is then pushed down by a
 * 208px hero is a worse first paint than one that never moves.
 */

const range = (n) => Array.from({ length: n }, (_, i) => i);

export default function CertificatesSkeleton() {
  return (
    <div className="certificates" aria-busy="true" aria-label={t('Loading certificates')}>
      <section className="cert-hero">
        <div className="cert-hero-info">
          <Skeleton w="96px" h={22} light pill />
          <Skeleton w="min(440px, 100%)" h={34} light />
          <Skeleton w="min(300px, 80%)" h={12} light />

          <div className="cert-hero-by">
            {/* Already a 24px translucent circle of its own — leave it empty. */}
            <span className="cert-hero-avatar" />
            <Skeleton w="88px" h={12} light />
            <Skeleton w="120px" h={12} light />
          </div>
        </div>

        {/* The image well, tinted but empty, at its own 472×208. */}
        <div className="cert-hero-preview" />
      </section>

      <section className="cert-list">
        <div className="cert-list-head">
          <div className="cert-list-head-left">
            <Skeleton w="14px" h={14} pill />
            <Skeleton w="150px" h={12} />
          </div>
          <Skeleton w="76px" h={32} />
        </div>

        <span className="cert-list-divider" />

        {/* One full row at the desktop grid; it reflows to 2 and 1 with the real one. */}
        <div className="cert-grid">
          {range(4).map((i) => (
            <div className="cert-item" key={i}>
              <div className="cert-thumb" />

              <div className="cert-item-meta">
                <Skeleton w="84px" h={11} />
                <Skeleton w="56px" h={10} />
              </div>

              {/* A neutral bar, not an empty `cert-download` — the real button
                  is a blue gradient, and one that can't be clicked yet would
                  look live rather than pending. */}
              <Skeleton w="100%" h={28.8} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
